import { isApiError } from "@compliance/domain";
import {
  Alert,
  Badge,
  Button,
  CloseButton,
  Code,
  Divider,
  Drawer,
  Group,
  Loader,
  Paper,
  Progress,
  ScrollArea,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconDownload,
  IconEye,
  IconFileText,
  IconWand,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  type ComplianceJob,
  useArtifactSignedUrl,
  useBatchReports,
  useGenerateEmtsReport,
  useJob,
  useReportPreview,
  useStartLcfsSuiteJob,
  useStartRfsSuiteJob,
} from "../../api/hooks";
import type { BatchDetail, Report } from "../../api/types";
import { useHasRole } from "../../auth";
import { DisabledReasonTooltip } from "../../components/DisabledReasonTooltip";
import { downloadUrl } from "../../lib/download";
import { notifyDownloadBlocked } from "../../lib/downloadNotify";
import { formatApiError } from "../../lib/errors";
import { invalidateBatchData } from "../../lib/invalidate";
import { jobStatusFailed, runningJobIdFrom, stageCopy } from "../../lib/runStages";
import { useParamPatcher, useStartKey } from "./useParamPatcher";

export function ReportsTab({ batch }: { batch: BatchDetail }) {
  const reportsQ = useBatchReports(batch.batch_id);
  const items = useMemo(() => reportsQ.data?.items ?? [], [reportsQ.data]);
  const signedUrl = useArtifactSignedUrl();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // Selected report lives in the URL (locked decision: URL is state of
  // record), so reloading or returning to a copied link reopens the same
  // drawer instead of losing the reviewer's place.
  const [params] = useSearchParams();
  const selectedReportId = params.get("report");
  const selected = useMemo(
    () => items.find((r) => r.report_id === selectedReportId) ?? null,
    [items, selectedReportId],
  );
  // Through the patcher, not `setSearchParams`: this drawer shares its URL with
  // the suite job ids, which poll while it is open, so a close landing in the
  // same tick as a job settling would otherwise drop one of the two.
  const patchParams = useParamPatcher();
  const openReport = useCallback(
    (reportId: string) => patchParams({ report: reportId }),
    [patchParams],
  );
  const closeReport = useCallback(() => patchParams({ report: null }), [patchParams]);

  // Generating is reviewer/admin only. Auditor and operations see the table +
  // the Download button (read-only). The real BE enforces this server-side via
  // the role gate on POST /reports/.../generate. Only the suite actions in the
  // header consume this — there is deliberately no per-report generate control:
  //
  // the row-level button used to post to /batches/{id}/reports/{short_name}/
  // generate, which never existed outside MSW. Against the live API every click
  // 404'd. Generation is per SET on this platform (the EMTS record, the RFS
  // monthly suite, the LCFS quarterly package), and it could not be otherwise
  // for every row: the quarterly RFS4800 and annual RFS0902 forms are recorded
  // under a closing-month batch but roll up a whole period, so regenerating one
  // needs that period's batch ids, which a batch-scoped control does not have.
  const canGenerate = useHasRole("reviewer", "admin");

  // POST /artifacts/report/{report_id}/signed-url. Returns a time-limited S3
  // URL, presigned with Content-Disposition: attachment, which downloadUrl
  // loads in a hidden frame so a blocked transfer can never take the portal
  // tab with it (BIO-308 #3).
  //
  // Mixed-mode caveat, which survives only under `pnpm dev` with MSW on: the
  // reports list is mocked there while /artifacts/.../signed-url always reaches
  // the real BE, and a report_id minted by MSW exists in no artifact store, so
  // the signed-url call 404s. Caught specifically, because the raw NOT_FOUND
  // reads as a missing artifact rather than as a mocked one.
  const onDownload = async (report: Report) => {
    setDownloadingId(report.report_id);
    try {
      const result = await signedUrl.mutateAsync({
        kind: "report",
        id: report.report_id,
        reason: `Download ${report.short_name} for ${batch.batch_id}`,
      });
      downloadUrl(result.url, displayFilenameFor(report), {
        onBlocked: () => notifyDownloadBlocked(report.title),
      });
    } catch (err) {
      if (isApiError(err) && err.httpStatus === 404) {
        notifications.show({
          title: "Report not yet on the backend",
          message:
            "The artifact endpoint is live, but this report's generation hasn't been wired to the backend yet — the report_id only exists locally. Generate against the live BE before downloading.",
          color: "yellow",
          autoClose: 7000,
        });
      } else {
        notifications.show({
          title: "Download failed",
          message: formatApiError(err),
          color: "red",
          autoClose: 5000,
        });
      }
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Title order={4}>Reports</Title>
          {canGenerate ? <ReportSuiteActions batchId={batch.batch_id} /> : null}
        </Group>
        <Text size="sm" c="dimmed">
          Generate the EMTS-Ready Batch Record, the RFS monthly suite, or the LCFS quarterly package
          — each produces a set of artifacts. Click a row to inspect the manifest.
        </Text>
      </Stack>

      <Paper withBorder radius="md">
        <ScrollArea>
          <Table
            verticalSpacing="md"
            horizontalSpacing="md"
            highlightOnHover
            stickyHeader
            miw={780}
            fz="sm"
          >
            <Table.Thead style={{ background: "var(--mantine-color-gray-0)" }}>
              <Table.Tr>
                <Table.Th>Report</Table.Th>
                <Table.Th style={{ width: 220 }}>Generated</Table.Th>
                <Table.Th style={{ width: 240 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((r) => (
                <Table.Tr
                  key={r.report_id}
                  style={{ cursor: "pointer" }}
                  onClick={() => openReport(r.report_id)}
                >
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <ThemeIcon variant="light" color="indigo" radius="md" size={32}>
                        <IconFileText size={16} />
                      </ThemeIcon>
                      <Stack gap={2}>
                        <Text size="sm" fw={600}>
                          {r.title}
                        </Text>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {displayFilenameFor(r)}
                        </Text>
                      </Stack>
                    </Group>
                  </Table.Td>
                  <Table.Td ff="monospace" fz="xs" c={r.generated_at ? undefined : "dimmed"}>
                    {r.generated_at ?? "—"}
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Button
                        data-tour="report-download"
                        size="xs"
                        variant="default"
                        leftSection={<IconDownload size={14} />}
                        onClick={() => onDownload(r)}
                        loading={downloadingId === r.report_id}
                        disabled={!r.generated_at}
                      >
                        Download
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        {/* Three states, not one. "Nothing here" used to render for all three:
            while the list was still loading, and when the request failed — so a
            fetch in flight and a 500 both read as a batch with no reports. */}
        {reportsQ.isPending ? (
          <Stack align="center" py="xl" gap="xs">
            <Loader size="sm" />
            <Text c="dimmed" size="sm">
              Loading reports…
            </Text>
          </Stack>
        ) : reportsQ.isError ? (
          <Alert
            m="md"
            color="red"
            variant="light"
            icon={<IconAlertTriangle size={16} />}
            title="Couldn't load this batch's reports"
          >
            {formatApiError(reportsQ.error)}
          </Alert>
        ) : items.length === 0 ? (
          <Stack align="center" py="xl" gap="xs">
            <Text c="dimmed" size="sm">
              No reports generated for this batch yet.
            </Text>
          </Stack>
        ) : null}
      </Paper>

      <ReportDrawer
        report={selected}
        onClose={closeReport}
        onDownload={onDownload}
        downloadingId={downloadingId}
      />
    </Stack>
  );
}

/**
 * Output file list for a report's manifest. The EMTS batch record bundles three
 * artifacts; every other report (incl. the RFS/LCFS suite rows) emits a single
 * deterministic `<ShortName>_<BatchID>.<ext>` — never the hard-coded EMTS trio,
 * which would mislabel a suite report's manifest.
 *
 * Single artifacts use the BE's own `filename` when it sent one. The quarterly
 * RFS4800 and annual RFS0902 seal as `.xlsx` workbooks, and this list used to
 * name them `.csv` (BIO-309).
 */
function outputFilesFor(report: Report): string[] {
  if (report.report_type === "rfs_emts_batch_record") {
    return [
      `TokenDataEntry_${report.batch_id}.csv`,
      `RINDataEntry_${report.batch_id}.csv`,
      `EmtsBatchRecord_${report.batch_id}.pdf`,
    ];
  }
  return [displayFilenameFor(report)];
}

/**
 * The artifact's deterministic name. Prefers the BE's own `filename` — it is what
 * the bytes were sealed under — and falls back to composing
 * `<ShortName>_<BatchID>.<ext>` for rows that don't carry one.
 */
export function displayFilenameFor(report: Report): string {
  return (
    report.filename ?? `${report.short_name}_${report.batch_id}.${primaryExtensionFor(report)}`
  );
}

/**
 * Extension of the report's PRIMARY downloadable artifact (the single blob the
 * signed URL resolves to), so a download isn't saved as `.csv` while carrying
 * other bytes (BIO-144 #7).
 *
 * The BE's `format` is authoritative — it is the sealed artifact's real format
 * (`report_generations.format`), and it has always been on the wire. The
 * report_type heuristic below is the fallback for rows without it (MSW mocks and
 * the lean generate envelopes): the EMTS batch record seals as a PDF, everything
 * else in the RFS/LCFS suites as CSV.
 */
export function primaryExtensionFor(report: Report): string {
  if (report.format) return report.format;
  return report.report_type === "rfs_emts_batch_record" ? "pdf" : "csv";
}

function ReportDrawer({
  report,
  onClose,
  onDownload,
  downloadingId,
}: {
  report: Report | null;
  onClose: () => void;
  onDownload: (r: Report) => void;
  downloadingId: string | null;
}) {
  // Preview-open + selected sheet live in the URL too (locked decision: URL
  // is state of record), so reloading or returning to a copied link reopens
  // the same view a reviewer was inspecting, not just the drawer itself.
  const [params] = useSearchParams();
  const previewOpen = params.get("preview") === "1";
  const sheet = params.get("sheet") ?? undefined;
  const canPreview = !!report?.generated_at && isPreviewableFormat(report);

  const patchParams = useParamPatcher();
  const setPreviewOpen = useCallback(
    (open: boolean) => patchParams({ preview: open ? "1" : null }),
    [patchParams],
  );
  const setSheet = useCallback((name: string) => patchParams({ sheet: name }), [patchParams]);

  // Reset per-report so opening a second drawer doesn't show the first's grid or
  // ask for a sheet that workbook doesn't have. Bookkeeping only — never
  // rendered — so a ref, not state; it doesn't need to trigger a re-render.
  const reportId = report?.report_id ?? null;
  const shownFor = useRef<string | null>(null);
  // `report` depends on the parent's reports query, which is still loading on
  // first mount — so the FIRST time this effect actually runs (reportId going
  // from null to the query resolving) is the URL's own restored report
  // catching up, not a user switching reports. Skip the clear only that once;
  // every later transition is a genuine change and clears as before.
  const hydrated = useRef(false);
  useEffect(() => {
    if (reportId === shownFor.current) return;
    shownFor.current = reportId;
    if (!hydrated.current) {
      hydrated.current = true;
      return;
    }
    if (previewOpen || sheet !== undefined) {
      patchParams({ preview: null, sheet: null });
    }
  }, [reportId, previewOpen, sheet, patchParams]);

  return (
    <Drawer
      opened={report !== null}
      onClose={onClose}
      position="right"
      size="xl"
      withCloseButton={false}
      padding="lg"
    >
      {report ? (
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Title order={4} mt={4}>
                {report.title}
              </Title>
              <Text size="xs" c="dimmed" ff="monospace">
                {report.short_name} · {report.report_type}
              </Text>
            </Stack>
            <CloseButton onClick={onClose} aria-label="Close" />
          </Group>

          <Divider />

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
              Output files
            </Text>
            <Code block fz="xs">
              {outputFilesFor(report).join("\n")}
            </Code>
          </Stack>

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
              Versions
            </Text>
            <Code block fz="xs">
              data_version: {report.data_version}
              {"\n"}software_version: {report.software_version ?? "(unset)"}
            </Code>
          </Stack>

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
              Generated
            </Text>
            <Text size="sm" ff="monospace">
              {report.generated_at ?? "Not generated yet"}
            </Text>
          </Stack>

          <Divider />

          <Group justify="flex-end" gap="xs">
            <DisabledReasonTooltip
              reason={canPreview ? null : previewDisabledReason(report)}
              position="top"
              w={240}
            >
              <Button
                data-tour="report-preview"
                size="xs"
                variant="default"
                leftSection={<IconEye size={14} />}
                disabled={!canPreview}
                onClick={() => setPreviewOpen(true)}
              >
                Preview
              </Button>
            </DisabledReasonTooltip>
            <Button
              size="xs"
              leftSection={<IconDownload size={14} />}
              onClick={() => onDownload(report)}
              loading={downloadingId === report.report_id}
              disabled={!report.generated_at}
            >
              Download
            </Button>
          </Group>

          {previewOpen ? (
            <ReportPreviewPanel report={report} sheet={sheet} onSelectSheet={setSheet} />
          ) : null}
        </Stack>
      ) : null}
    </Drawer>
  );
}

/** Formats the BE renders as a cell grid (`compliance.reports.preview`). */
const PREVIEWABLE_FORMATS = new Set(["xlsx", "csv"]);

export function isPreviewableFormat(report: Report): boolean {
  return PREVIEWABLE_FORMATS.has(primaryExtensionFor(report));
}

function previewDisabledReason(report: Report): string {
  if (!report.generated_at) return "Generate the report first.";
  return `A .${primaryExtensionFor(report)} artifact has no inline preview — download it instead.`;
}

/**
 * The artifact's own cells, so a preparer can review a draft workbook before
 * approving the batch (BIO-309).
 *
 * The bytes are read server-side from the archive bucket, so this works when the
 * browser-side presigned download does not, and an `.xlsx` — which a browser
 * cannot render from a URL at all — is readable in place. A draft is labelled a
 * DRAFT: it is an early preview, not a filing.
 */
function ReportPreviewPanel({
  report,
  sheet,
  onSelectSheet,
}: {
  report: Report;
  sheet: string | undefined;
  onSelectSheet: (sheet: string) => void;
}) {
  const preview = useReportPreview(report.batch_id, report.report_id, sheet);

  if (preview.isPending) {
    return (
      <Stack gap="xs">
        <Divider label="Preview" labelPosition="left" />
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Reading the artifact…
          </Text>
        </Group>
      </Stack>
    );
  }

  if (preview.isError || !preview.data) {
    // Same hybrid seam the download path already explains: in dev the report
    // list + generate are MSW-backed and mint local-only report_ids, while this
    // read bypasses to FastAPI — which cannot know them. Say so instead of
    // showing a bare NOT_FOUND, and never fabricate cells for a compliance
    // artifact.
    const hybridSeam = isApiError(preview.error) && preview.error.httpStatus === 404;
    return (
      <Stack gap="xs">
        <Divider label="Preview" labelPosition="left" />
        <Alert
          color={hybridSeam ? "yellow" : "red"}
          variant="light"
          icon={<IconAlertTriangle size={16} />}
        >
          <Text size="sm">
            {hybridSeam
              ? "This report was generated locally by the mock backend, so the real artifact store has no bytes to preview. Generate against the live backend to review the workbook."
              : formatApiError(preview.error)}
          </Text>
        </Alert>
      </Stack>
    );
  }

  const data = preview.data;
  return (
    <Stack gap="xs">
      <Divider label="Preview" labelPosition="left" />
      <Group gap="xs" wrap="wrap">
        <Badge color="orange" variant="filled" size="sm">
          DRAFT — not filed
        </Badge>
        <Text size="xs" c="dimmed" ff="monospace">
          {data.filename}
        </Text>
      </Group>
      <Text size="xs" c="dimmed">
        An early preview of the current figures. Nothing here has been submitted to a regulator.
      </Text>

      {data.sheets.length > 1 ? (
        <Group gap={6} wrap="wrap">
          {data.sheets.map((name) => (
            <Button
              key={name}
              size="compact-xs"
              variant={name === data.sheet ? "filled" : "default"}
              onClick={() => onSelectSheet(name)}
            >
              {name}
            </Button>
          ))}
        </Group>
      ) : null}

      <ScrollArea.Autosize mah={420}>
        <Table withTableBorder withColumnBorders fz="xs" striped>
          <Table.Tbody>
            {data.rows.map((row, rowIndex) => (
              // The grid is positional — a row's identity IS its index in the
              // sheet, and two rows can hold identical cells.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional grid
              <Table.Tr key={rowIndex}>
                <Table.Td c="dimmed" ff="monospace" style={{ width: 36 }}>
                  {rowIndex + 1}
                </Table.Td>
                {row.map((cell, cellIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional grid
                  <Table.Td key={cellIndex} style={{ whiteSpace: "nowrap" }}>
                    {cell}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
      {data.rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          This sheet is empty.
        </Text>
      ) : null}
      {data.formulas_uncalculated ? (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Text size="xs">
            Some cells are Excel formulas the workbook carries no computed result for, so they are
            shown as written. Open the downloaded workbook before relying on those values.
          </Text>
        </Alert>
      ) : null}
      {data.truncated ? (
        <Text size="xs" c="dimmed">
          Showing the first {data.rows.length} row(s)
          {data.total_rows ? ` of ${data.total_rows}` : ""} — download the artifact for the rest.
        </Text>
      ) : null}
    </Stack>
  );
}

type Suite = "EMTS" | "RFS" | "LCFS";

/** The two suites whose render is long enough to need the job machinery. */
type JobSuite = Extract<Suite, "RFS" | "LCFS">;

type SuiteJob = {
  begin: () => Promise<void>;
  /** A job id exists (in the URL) — the button is committed to a run. */
  active: boolean;
  starting: boolean;
  job: ComplianceJob | null;
};

/**
 * URL-backed job state for one suite: start it, poll it, surface its outcome.
 *
 * Extracted from the RFS-only version (BIO-346) because LCFS needed exactly
 * this and a second copy of the terminal-state reasoning below would drift. The
 * only per-suite inputs are the start mutation, the URL param prefix, and the
 * words used in the notifications.
 *
 * The job id lives in the URL (locked decision: URL is state of record), not
 * component state — it survives a leave-and-return, and it is what makes the
 * suite button "active" (disabling a repeat click) the instant the start request
 * resolves, rather than waiting on the first poll response.
 */
function useSuiteJob({
  batchId,
  suite,
  noun,
  paramPrefix,
  start,
  patchParams,
}: {
  batchId: string;
  suite: JobSuite;
  noun: string;
  paramPrefix: string;
  start: ReturnType<typeof useStartRfsSuiteJob>;
  patchParams: (updates: Record<string, string | null>) => void;
}): SuiteJob {
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const jobParam = `${paramPrefix}Job`;
  const startKeyParam = `${paramPrefix}StartKey`;
  const jobId = params.get(jobParam);
  const setJobId = useCallback(
    (id: string | null) => {
      patchParams({ [jobParam]: id });
    },
    [patchParams, jobParam],
  );
  // A start request whose response never arrived (network drop, timeout) is
  // ambiguous — the backend may have already created the job. `useStartKey`
  // reuses the SAME idempotency key on retry (rather than apiFetch's default
  // fresh uuid per call) so the backend's idempotency store treats a retry as
  // the same intent instead of running a second suite concurrently; the backend
  // releases the reservation on failure, so reusing the key after ANY failure
  // (not just ambiguous ones) is safe. Released only once a job id is
  // confirmed — at that point jobId is the thing being tracked.
  const startKey = useStartKey(startKeyParam);
  const job = useJob(jobId);

  // Surface the job's outcome exactly once, when it reaches a terminal state.
  // Without this the user gets a progress bar that silently disappears — the
  // indefinite-spinner problem in a new costume. Only an authoritative signal
  // counts as terminal: the job's own done/error state, or a definitive status
  // check failure — see `jobStatusFailed`, which the recompute control shares.
  const statusFailed = jobStatusFailed(job);
  const settled = job.data?.state === "done" || job.data?.state === "error" || statusFailed;
  useEffect(() => {
    if (!settled) return;
    if (job.data?.state === "done") {
      const count = Array.isArray(job.data.result?.reports)
        ? (job.data.result?.reports as unknown[]).length
        : 0;
      notifications.show({
        title: `${suite} ${noun} generated`,
        message: `${count} artifact${count === 1 ? "" : "s"} generated.`,
        color: "teal",
        autoClose: 4000,
      });
    } else if (job.data?.state === "error") {
      notifications.show({
        title: `${suite} generation failed`,
        message: job.data.last_error ?? "The job ended without a result.",
        color: "red",
        autoClose: 8000,
      });
    } else {
      notifications.show({
        title: `${suite} generation status unavailable`,
        message: "Could not confirm the job's status. Generate again if it didn't finish.",
        color: "red",
        autoClose: 8000,
      });
    }
    setJobId(null);
    void invalidateBatchData(queryClient, batchId);
  }, [settled, job.data, queryClient, batchId, setJobId, suite, noun]);

  const begin = async () => {
    const key = startKey.take();
    try {
      const started = await start.mutateAsync({ batchId, idempotencyKey: key });
      patchParams({ ...startKey.release(), [jobParam]: started.job_id });
    } catch (err) {
      // A 409 CONCURRENT_MODIFICATION is not a failure to start — a render for
      // this batch is already in flight and the backend hands back its id.
      // Adopt it rather than reporting an error: the operator asked to watch
      // this package being built, and it is being built. Without this they get
      // an error toast and no route to the running job, which is the same
      // outcome-invisibility this ticket exists to remove.
      const running = runningJobIdFrom(err);
      if (running) {
        patchParams({ ...startKey.release(), [jobParam]: running });
        notifications.show({
          title: `${suite} ${noun} already generating`,
          message: "Attached to the run already in progress.",
          color: "blue",
          autoClose: 4000,
        });
        return;
      }
      notifications.show({
        title: `${suite} generation failed to start`,
        message: formatApiError(err),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  return { begin, active: jobId !== null, starting: start.isPending, job: job.data ?? null };
}

/**
 * Suite-generation buttons: each maps to a dedicated BE endpoint that seals a
 * whole report set in one call —
 *   - EMTS  → POST /batches/{id}/reports/emts/generate        (3 artifacts)
 *   - RFS   → POST /batches/{id}/reports/rfs/generate-async   (10 artifacts)
 *   - LCFS  → POST /batches/{id}/reports/lcfs/generate-async  (14 artifacts)
 * EMTS returns ReportMetadata[] directly; the two suites return a job to poll.
 * On success we refresh the batch surface via invalidateBatchData so the reports
 * table re-renders with the freshly sealed rows and the audit timeline picks up
 * the generation event. The per-row Generate buttons below remain for one-off
 * regeneration of an individual report.
 *
 * Generating a report set is a routine step, so each button seals the set
 * directly on click — no audit reason is collected (the audit entry records the
 * generation with a null reason).
 */
function ReportSuiteActions({ batchId }: { batchId: string }) {
  const emts = useGenerateEmtsReport();
  const queryClient = useQueryClient();
  // Both suites run as polled jobs. RFS moved first (BIO-308 #4) because a cold
  // ten-artifact render already exceeded a reasonable request budget; LCFS
  // followed for a blunter reason (BIO-346) — its loader reads dairy NET per
  // dairy off the live DUT, measured at ~200-240s for that loop alone, so the
  // synchronous response was being severed by the 300s ALB idle timeout while
  // the work ran on to a commit the caller never saw. EMTS stays synchronous;
  // three artifacts off already-computed figures is genuinely fast.
  const patchParams = useParamPatcher();
  const rfs = useSuiteJob({
    batchId,
    suite: "RFS",
    noun: "suite",
    paramPrefix: "rfs",
    start: useStartRfsSuiteJob(),
    patchParams,
  });
  const lcfs = useSuiteJob({
    batchId,
    suite: "LCFS",
    noun: "package",
    paramPrefix: "lcfs",
    start: useStartLcfsSuiteJob(),
    patchParams,
  });

  const generateEmts = async () => {
    try {
      const artifacts = await emts.mutateAsync({ batchId });
      notifications.show({
        title: "EMTS record generated",
        message: `${artifacts.length} artifact${artifacts.length === 1 ? "" : "s"} generated.`,
        color: "teal",
        autoClose: 4000,
      });
      await invalidateBatchData(queryClient, batchId);
    } catch (err) {
      notifications.show({
        title: "EMTS generation failed",
        message: formatApiError(err),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  return (
    <Group gap="xs" wrap="wrap" justify="flex-end">
      <Button
        color="indigo"
        leftSection={<IconWand size={16} />}
        onClick={generateEmts}
        loading={emts.isPending}
        data-tour="report-emts"
      >
        EMTS Record
      </Button>
      <SuiteJobButton label="RFS suite" color="indigo" tour="report-rfs" {...rfs} />
      <SuiteJobButton label="LCFS package" color="teal" tour="report-lcfs" {...lcfs} />
    </Group>
  );
}

/**
 * Suite trigger with real progress (BIO-308 #4, generalized by BIO-346).
 *
 * The old button showed an indefinite spinner — ~56 seconds for RFS, several
 * minutes for LCFS — with no progress, no timeout and no recovery action, so a
 * user could not tell a slow render from a dead one. Progress is a count of
 * generated REPORTS out of the suite's total — the unit the backend advances,
 * and the one an operator can act on — and the terminal state always surfaces
 * (see the effect in useSuiteJob) so the bar never just vanishes.
 *
 * The unit matters because it was wrong: the backend set `total` to the artifact
 * count (10 for RFS, 14 for LCFS — a CSV and a PDF cover each) while advancing
 * once per report, so a finished suite sat at "5 of 10" and the bar stopped at
 * half. Both are reports now.
 *
 * `active` (a job id exists, in the URL) rather than `job`'s poll data is what
 * disables the plain button — the id is set the instant the start request
 * resolves, before the first poll response populates `job`, so a repeat click
 * in that gap can no longer fire a second `generate-async` request.
 */
function SuiteJobButton({
  label,
  color,
  tour,
  begin,
  starting,
  active,
  job,
}: SuiteJob & { label: string; color: string; tour?: string }) {
  if (!active) {
    return (
      <Button
        variant="light"
        color={color}
        leftSection={<IconWand size={16} />}
        onClick={() => void begin()}
        loading={starting}
        data-tour={tour}
      >
        {label}
      </Button>
    );
  }

  const total = job?.total ?? 0;
  const done = job?.progress ?? 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  // The count alone is not a progress report for most of this run. Nothing can
  // be counted until the first artifact is sealed, and everything before that —
  // the per-dairy DUT load, the coverage gate, the build — is where the minutes
  // go: a good LCFS August sat at "0 of 7" for 7m35s of a 7m36s render. The
  // stage is what moves in that window, so it leads and the count follows it.
  const stage = stageCopy(job?.stage);
  return (
    <Tooltip label="Generation runs in the background — you can leave this page.">
      <Stack gap={4} miw={200} data-tour={tour ? `${tour}-progress` : undefined}>
        <Text size="xs" c="dimmed">
          {label} · {stage ?? `${done} of ${total || "?"} generated`}
        </Text>
        {stage ? (
          <Text size="xs" c="dimmed">
            {done} of {total || "?"} generated
          </Text>
        ) : null}
        <Progress value={pct} size="sm" radius="sm" color={color} striped animated />
      </Stack>
    </Tooltip>
  );
}
